import React, { useEffect, useRef, useState } from 'react';
import { Modal, Checkbox, Button } from '@bedrock/components';
import classNames from 'classnames/bind';
import committeeLogo from './assets/committee-logo.png';
import jubaoQrcode from './assets/jubao-qrcode-2b216a.png';
import neteaseLogo from './assets/netease-logo.svg';
import styles from './index.less';
import { setDismissForDays, shouldShow } from './storage';

const cx = classNames.bind(styles);

const COUNTDOWN_SECONDS = 5;
const DISMISS_DAYS = 30;
const PAPER_URL = 'https://cmyq.dingwei.netease.com/survey/ethical/paper';

interface Props {
  mail: string;
}

const IntegrityModalGate: React.FC<Props> = ({ mail }) => {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [dismissChecked, setDismissChecked] = useState(true);
  const checkedRef = useRef(true);

  useEffect(() => {
    if (shouldShow(mail)) {
      setVisible(true);
      setCountdown(COUNTDOWN_SECONDS);
      setDismissChecked(true);
      checkedRef.current = true;
    }
  }, [mail]);

  useEffect(() => {
    if (!visible) return;
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [visible, countdown]);

  const handleConfirm = () => {
    if (countdown > 0) return;
    if (checkedRef.current) {
      setDismissForDays(mail, DISMISS_DAYS);
    }
    // 未勾选 → 不写 localStorage,按 PRD"未点击则当天继续提示",每次进入都再弹
    setVisible(false);
  };

  const handleCheck = (checked: boolean) => {
    setDismissChecked(checked);
    checkedRef.current = checked;
  };

  return (
    <Modal
      className={cx('integrity-modal')}
      visible={visible}
      width={680}
      maskClosable={false}
      keyboard={false}
      closable={false}
      footer={null}
      destroyOnClose
    >
      <div className={cx('integrity-modal__header')}>
        <div className={cx('integrity-modal__header-text')}>
          <div className={cx('integrity-modal__title')}>
            <span>诚信同行须知：</span>
            <span className={cx('integrity-modal__title-highlight')}>
              网易商业道德规范提醒
            </span>
          </div>
          <div className={cx('integrity-modal__sublines')}>
            <div className={cx('integrity-modal__subline')}>
              举报网站：
              <a
                className={cx('integrity-modal__subline-link')}
                href="https://jubao.163.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                jubao.163.com
              </a>
            </div>
            <div className={cx('integrity-modal__subline')}>
              举报邮箱：
              <a
                className={cx('integrity-modal__subline-link')}
                href="mailto:jubao@corp.netease.com"
              >
                jubao@corp.netease.com
              </a>
            </div>
          </div>
        </div>
        <div className={cx('integrity-modal__qrcode')}>
          <img
            className={cx('integrity-modal__qrcode-img')}
            src={jubaoQrcode}
            alt="网易廉正举报"
          />
          <div className={cx('integrity-modal__qrcode-label')}>
            网易廉正举报
          </div>
        </div>
      </div>

      <div className={cx('integrity-modal__content')}>
        <div>
          <p className={cx('integrity-modal__greeting')}>尊敬的合作伙伴：</p>
          <p className={cx('integrity-modal__paragraph')}>
            感谢您一直以来与网易的结伴同行！网易始终保持热爱与创新，恪守诚信正直的商业道德规范，对触碰廉洁底线的行为，始终坚持零容忍。若网易员工（含历史员工）与贵司合作中，有任何违反商业道德的行为，均可向网易公司反映。
          </p>
          <p className={cx('integrity-modal__paragraph')}>
            我们真切邀请您点击学习 《
            <a
              className={cx('integrity-modal__link')}
              href={PAPER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              网易商业道德责任书
            </a>
            》
            ，不与网易员工发生任何形式的行贿以及与网易构成利益冲突的行为，包括但不限于馈赠、回扣、宴请、私下有偿兼职、私下合作入股等违规行为。
          </p>
          <div className={cx('integrity-modal__signature')}>网易道德委员会</div>
        </div>

        <div className={cx('integrity-modal__divider')} />

        <div className={cx('integrity-modal__tip')}>
          <div>温馨提示：</div>
          <ul>
            <li>
              完成网易商业道德责任书学习后，别忘了最后一步——在页面底部登记您的信息。感谢支持与配合！
            </li>
          </ul>
        </div>
      </div>

      <div className={cx('integrity-modal__footer')}>
        <div className={cx('integrity-modal__action')}>
          <Checkbox
            checked={dismissChecked}
            onChange={(e) => handleCheck(e.target.checked)}
          >
            30天内不再提醒
          </Checkbox>
          <Button
            type="primary"
            className={cx('integrity-modal__ok-btn')}
            disabled={countdown > 0}
            onClick={handleConfirm}
          >
            {countdown > 0 ? `已阅读 (${countdown}s)` : '已阅读'}
          </Button>
        </div>
        <div className={cx('integrity-modal__brand')}>
          <img
            className={cx('integrity-modal__brand-netease')}
            src={neteaseLogo}
            alt="NetEase"
          />
          <div className={cx('integrity-modal__brand-divider')} />
          <div className={cx('integrity-modal__brand-product')}>
            <img
              className={cx('integrity-modal__brand-logo')}
              src={committeeLogo}
              alt="committee"
            />
            <span>网易道德委员会</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default IntegrityModalGate;
